import { useDeleteAgenda } from 'src/suitability-report/hooks/useDeleteAgenda';
import { useDeleteDataExport } from 'src/suitability-report/hooks/useDeleteDataExport';
import { useDeleteSuitabilityReport } from 'src/suitability-report/hooks/useDeleteSuitabilityReport';
import { useDeleteMeetingNote } from 'src/meeting-note/hooks/useDeleteMeetingNote';
import { GeneratedDocumentType } from '../components/generatedDocumentTypes';

export function useDeleteGeneratedDocument() {
  const {
    deleteSuitabilityReport,
    deleteFamilySuitabilityReport,
    error: suitabilityError,
    reset: resetSuitability,
    isLoading: isDeletingSuitability,
  } = useDeleteSuitabilityReport();
  const {
    deleteMeetingAgenda,
    deleteFamilyMeetingAgenda,
    error: agendaError,
    reset: resetAgenda,
    isLoading: isDeletingAgenda,
  } = useDeleteAgenda();
  const {
    deleteDataExport,
    deleteFamilyDataExport,
    error: dataExportError,
    reset: resetDataExport,
    isLoading: isDeletingDataExport,
  } = useDeleteDataExport();
  const {
    deleteMeetingNote,
    deleteFamilyMeetingNote,
    error: meetingNoteError,
    reset: resetMeetingNote,
    isLoading: isDeletingMeetingNote,
  } = useDeleteMeetingNote();

  const deleteDocument = async (
    clientId: string,
    id: string,
    documentType: GeneratedDocumentType,
  ) => {
    switch (documentType) {
      case 'suitability-report':
        await deleteSuitabilityReport(clientId, id);
        break;
      case 'meeting-agenda':
        await deleteMeetingAgenda(id);
        break;
      case 'data-export':
        await deleteDataExport(clientId, id);
        break;
      case 'meeting-note':
        await deleteMeetingNote(clientId, id);
        break;
    }
  };

  const deleteFamilyDocument = async (
    familyId: string,
    id: string,
    documentType: GeneratedDocumentType,
  ) => {
    switch (documentType) {
      case 'suitability-report':
        await deleteFamilySuitabilityReport(familyId, id);
        break;
      case 'meeting-agenda':
        await deleteFamilyMeetingAgenda(familyId, id);
        break;
      case 'data-export':
        await deleteFamilyDataExport(familyId, id);
        break;
      case 'meeting-note':
        await deleteFamilyMeetingNote(familyId, id);
        break;
    }
  };

  const errorsByType: Record<GeneratedDocumentType, { message: string } | null> = {
    'suitability-report': suitabilityError,
    'meeting-agenda': agendaError,
    'data-export': dataExportError,
    'meeting-note': meetingNoteError,
  };

  const deleteErrorFor = (documentType: GeneratedDocumentType) => errorsByType[documentType];

  const resetErrors = () => {
    resetSuitability();
    resetAgenda();
    resetDataExport();
    resetMeetingNote();
  };

  const isDeleting =
    isDeletingSuitability || isDeletingAgenda || isDeletingDataExport || isDeletingMeetingNote;

  return { deleteDocument, deleteFamilyDocument, deleteErrorFor, resetErrors, isDeleting };
}
